import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { APIEntityType } from '../enums/api-entity-type.enum';
import { BillingItemKind } from '../enums/billing.enums';
import { InsuranceTypeEnum } from '../enums/insurance-type.enum';
import { APIResponse } from './types/api-response.type';
import { InsuranceOfferRequest, InsuranceOfferResponse } from './types/offers.types';
import {
  BillingDocument,
  CreateProposalMultipleRequest,
  CreateProposalMultipleResponse,
  CreateProposalResponse,
  CustomerBillingResponse,
  GetLeadDetailsRequest,
  GetProposalDetailsRequest,
  Lead,
  Proposal,
  ProposalDetails,
  ProposalFetchRequest,
} from './types/proposals.type';
import { UserAuthService } from './user-auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProposalService {
  insurancebaseURL: string;

  catalogueBaseURL: string;

  baseHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  constructor(
    private httpClient: HttpClient,
    private userAuthService: UserAuthService,
  ) {
    this.insurancebaseURL = environment.INSURANCEAPIURL;
    this.catalogueBaseURL = environment.CATALOGUEAPIURL;
  }

  getInsurancesOffers(body: InsuranceOfferRequest) {
    const headers = this.baseHeaders
      .set('Cache-Control', 'no-cache, no-store, must-revalidate')
      .set('Pragma', 'no-cache')
      .set('Expires', '0')
      .set('X-Request-ID', Date.now().toString());

    return this.httpClient.post<APIResponse<InsuranceOfferResponse>>(
      `${this.insurancebaseURL}offers`,
      body,
      {
        headers,
      },
    );
  }

  addProposalRequest(
    body: Omit<Proposal, 'userId'>,
  ): Observable<APIResponse<CreateProposalResponse>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token,
    );
    const fullBody: Proposal = {
      ...body,
      userId: this.userAuthService.getCurrentUser().userId,
    };

    return this.httpClient.post<CreateProposalResponse>(
      `${this.insurancebaseURL}request/proposal`,
      fullBody,
      {
        headers,
      },
    );
  }

  addProposalRequestMultiple(
    bodies: Omit<Proposal, 'userId'>[],
  ): Observable<APIResponse<CreateProposalMultipleResponse>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token,
    );
    const requests: Proposal[] = [];
    bodies.forEach((body) => {
      requests.push({
        ...body,
        userId: this.userAuthService.getCurrentUser().userId,
      });
    });
    const fullBody: CreateProposalMultipleRequest = {
      requests,
    };

    return this.httpClient.post<APIResponse<CreateProposalMultipleResponse>>(
      `${this.insurancebaseURL}request/proposal/multiple`,
      fullBody,
      {
        headers,
      },
    );
  }

  getProposals(
    body: Omit<ProposalFetchRequest, 'entityId'>,
  ): Observable<APIResponse<{ proposalsList: ProposalDetails[] }>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token,
    );
    const fullBody: ProposalFetchRequest = {
      ...body,
      entityId: this.userAuthService.getCurrentUser().userId,
    };
    return this.httpClient.post<APIResponse<{ proposalsList: ProposalDetails[] }>>(
      `${this.insurancebaseURL}get/proposals`,
      fullBody,
      {
        headers,
      },
    );
  }

  getLeadDetails(leadId: string): Observable<APIResponse<Lead>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token,
    );
    const body: GetLeadDetailsRequest = {
      leadId,
      entityType: APIEntityType.USER_ID,
      entityId: this.userAuthService.getCurrentUser().userId,
    };
    return this.httpClient.post<APIResponse<Lead>>(
      `${this.insurancebaseURL}get/lead/details`,
      body,
      {
        headers,
      },
    );
  }

  getProposalDetails(proposalId: string): Observable<APIResponse<ProposalDetails>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token,
    );
    const body: GetProposalDetailsRequest = {
      proposalId,
    };
    return this.httpClient.post<APIResponse<ProposalDetails>>(
      `${this.insurancebaseURL}get/proposal`,
      body,
      {
        headers,
      },
    );
  }

  getCustomerBilling(proposalId: string): Observable<APIResponse<CustomerBillingResponse>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token,
    );
    return this.httpClient.get<APIResponse<CustomerBillingResponse>>(
      `${this.insurancebaseURL}proposal/${proposalId}/customer-billing`,
      { headers },
    );
  }

  getCustomerBillingDocument(
    kind: BillingItemKind,
    id: string,
  ): Observable<APIResponse<BillingDocument>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token,
    );
    return this.httpClient.get<APIResponse<BillingDocument>>(
      `${this.insurancebaseURL}customer-billing/${kind}/${id}/document`,
      { headers },
    );
  }

  getExtendedProposalDetails(proposalId: string): Observable<APIResponse<ProposalDetails>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token,
    );
    const body: GetProposalDetailsRequest = {
      proposalId,
    };
    return this.httpClient.post<APIResponse<ProposalDetails>>(
      `${this.insurancebaseURL}get/proposal/extended`,
      body,
      {
        headers,
      },
    );
  }

  getOffersValidityYear(insuranceType: InsuranceTypeEnum): Observable<number> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token,
    );

    return this.httpClient.get<number>(`${this.catalogueBaseURL}offer/get/offers-validity-year`, {
      headers,
      params: {
        insuranceType,
      },
    });
  }
}
